import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { SignaturePadComponent } from 'src/app/shared/components/signature-pad/signature-pad.component';
import { IPharmacy } from 'src/app/shared/interfaces/pharmacy.interface';
import { PioneerRxConsentFormPdfService } from 'src/app/settings/components/dialogs/pms-integration-dialog/pioneer-rx-consent-form/pioneer-rx-consent-form-pdf.service';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { PharmacyManagementSystemIntegrationStepsEnum } from 'src/app/settings/interfaces/PharmacyManagementSystemIntegrationStepsEnum';
import { PinService } from 'src/app/shared/services/pin.service';
import { SettingsState } from 'src/app/shared/states/settings/settings.state';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CreatePioneerRxIntegration } from 'src/app/shared/states/settings/settings.actions';
import { take, withLatestFrom } from 'rxjs/operators';
import { AlertTypesEnum } from 'src/app/shared/interfaces/AlertTypesEnum';
import { IAlertMessage } from 'src/app/shared/interfaces/alert-message.interface';
@UntilDestroy()
@Component({
  selector: 'app-pioneer-rx-consent-form',
  templateUrl: './pioneer-rx-consent-form.component.html',
  styleUrls: ['.././pms-integration-stepper-shared.component.scss' ,'./pioneer-rx-consent-form.component.scss']
})
export class PioneerRxConsentFormComponent implements OnInit, OnChanges {
  @Input() progress: PharmacyManagementSystemIntegrationStepsEnum;

  @Output() back: EventEmitter<PharmacyManagementSystemIntegrationStepsEnum> = new EventEmitter();
  @Output() next: EventEmitter<PharmacyManagementSystemIntegrationStepsEnum> = new EventEmitter();

  @Output() ok: EventEmitter<any> = new EventEmitter();
  
  @ViewChild('signature')
  public signaturePad: SignaturePadComponent;

  @ViewChild('doc')
  public documentWithSignature;

  pdfUrl;
  zoom: number = 1.2;

  isFormSigned: boolean;
  isFormCompleted: boolean;

  PharmacyManagementSystemIntegrationStepsEnum = PharmacyManagementSystemIntegrationStepsEnum;

  pinDialogChannel: string = "CONSENT_FORM";
  alertMessage: IAlertMessage = {text: 'Your consent form has been signed and sent to PioneerRx', type: AlertTypesEnum.Success};
  showMessage: boolean;

  @Select(SettingsState.loading) loading$: Observable<boolean | undefined>;
  @Select(SettingsState.success) success$: Observable<boolean | undefined>;

  constructor(
    private store: Store,
    private pioneerRxPdfService: PioneerRxConsentFormPdfService,
    private pinService: PinService
    ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.progress.currentValue === PharmacyManagementSystemIntegrationStepsEnum.Finish) {
      this.showMessage = true;
    }
  }

  ngOnInit(): void {
    if (this.progress !== PharmacyManagementSystemIntegrationStepsEnum.Finish) {
      this.populatePDFDocWithStaticData();
    }
   
    this.pinService.validatePin$
    .pipe(untilDestroyed(this))
    .subscribe(res => {
      if (res?.dialogId === this.pinDialogChannel && res?.success) {
        this.handleIntegration(res.response, res.pin)
      }
    })
  }

  private async handleIntegration(pinResponse, pin): Promise<void> {
    const pdfBlob = await this.pioneerRxPdfService.addName(pinResponse.user.firstName, pinResponse.user.lastName);
    this.pinService.closeDialog();
    let payload = new FormData();
    payload.append('file', pdfBlob);
    payload.append('pin', pin);
    this.store.dispatch(new CreatePioneerRxIntegration(payload))
    .pipe(take(1), withLatestFrom(this.success$))
    .subscribe(([_, success]) => {
      if (success) {
        this.isFormCompleted = true;
        this.next.emit(PharmacyManagementSystemIntegrationStepsEnum.Finish);
      } else {
        this.alertMessage = {text: 'Error connecting:  Please try again.', type: AlertTypesEnum.Error};
      }
      this.showMessage = true;
    });
  }

  async populatePDFDocWithStaticData(): Promise<void> {
    const pharmacy = this.store.selectSnapshot(PharmacyState.currentPharmacy) as IPharmacy;
    const pdfBlob = await this.pioneerRxPdfService.addStaticInformation(pharmacy.name, pharmacy.npiNumber, pharmacy.address, new Date().toLocaleDateString());
    
    const pdfBlobUrl = URL.createObjectURL(pdfBlob);

    this.pdfUrl = pdfBlobUrl;
  }

  async drawComplete(event: MouseEvent | Touch) {
    const dataUrl = this.signaturePad.toDataURL();
    const pdfBlob = await this.pioneerRxPdfService.addSignature(dataUrl);
    const pdfBlobUrl = URL.createObjectURL(pdfBlob);
    this.pdfUrl = pdfBlobUrl;
    this.isFormSigned = true;
  }

  signatureCleared(): void {
   this.isFormSigned = false;
  }

  scrollDown(): void {
    this.documentWithSignature.nativeElement.scrollTo({
      top: this.documentWithSignature.nativeElement.scrollHeight,
      behavior: "smooth",
    });
  }

  isSignaturePadVisible(): boolean {
    const scrollHeight = this.documentWithSignature?.nativeElement?.scrollHeight || 0;
    const scrollTop = this.documentWithSignature?.nativeElement?.scrollTop || 0;
    if (scrollHeight !== 0 && scrollTop !== 0) {
      return scrollHeight - scrollTop <= 800;
    }
    return false;
  }

  showPin(): void {
    this.pinService.setDialogId(this.pinDialogChannel);
    this.pinService.showPinDialog$.next(true);
  }

  OK(): void {
    this.ok.next();
  }

}
